<template>
	<div>
		<loader-component v-if="loading" :loader-message="'Fetching Market Data'"></loader-component>

		<div class="mt-3" v-else>
			<trade-summary-component v-if="isSummary" :trade="form" @submit-request="submitTrade('process')" @cancel-request="cancelEquitiesRequest" />

			<div v-else>
				<div v-if="isEmpty(marketData)">
					<h5 class="tx-color-01 mg-b-5 tx-center">Empty.</h5>
					<p class="tx-color-03 tx-13 tx-center">No Market Data available at the moment.</p>
				</div>
				<div v-else>
					<trade-cash-balance-component :currency="'GHS'" :show-add-fund="true" />
					<alert-success v-if="submitted" :form="form" :class="'shadow-sm'" message="Your order has been submitteed."></alert-success>

					<trade-counter-component
						v-if="showTradeCounter"
						:form="form"
						:loading-counter="loadingTradeCounterData"
						@remove-equity="removeEquity"
						@calculate-trade-cost="calculateTradeCost"
						@set-action="setTradeAction"
						@update-counter-status="updateloadingTradeCounterStatus"
						@remove-all="removeAll"
					/>
					<market-data-component :market-data="marketData" :year-performances="yearPerformances" @select-equity="selectEquity" />
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import axios from 'axios';
import { trade } from '../../../../mixins';
import { mapGetters } from 'vuex';

export default {
	mixins: [trade.equities],
	mounted() {
		this.fetchData();
	},
	data() {
		const cashBalance = this.$store.getters['trade/cashBalance'];

		return {
			isSummary: false,
			submitted: false,
			loading: true,
			showTradeCounter: false,
			loadingTradeCounterData: true,
			marketData: [],
			yearPerformances: {},
			portfolio: {},
			currentBalance: cashBalance,
			form: new Form({
				cash_balance: cashBalance,
				account_code: null,
				action: null,
				type: 'equity',
				portfolio_id: null,
				trade_data: [],
				trade_cost: {},
			}),
		};
	},
	computed: {
		...mapGetters({
			user: 'user',
			company: 'company',
		}),
	},
	methods: {
		fetchPortfolio() {
			return axios.get(`/api/companies/${this.company.external_client_id}/portfolios`, {
				params: {
					portfolio_type_id: 1,
					noAllocation: true,
				},
			});
		},
		cancelEquitiesRequest() {
			this.form.action = 'validate';
			this.isSummary = false;
		},
		checkHolding(stock) {
			const { holdings } = this.portfolio;

			const holding = holdings.find((obj) => obj.security_details.stock_code === stock);

			if (holding) {
				return this.$options.filters.toNumber(holding.units);
			}
		},
		calculateTradeCost({ request, params }) {
			const { charges, consideration, net_consideration, equity_price_band } = params;

			request.charges = charges;
			request.consideration = consideration;
			request.net_consideration = net_consideration;
			request.equity_price_band = equity_price_band;

			this.form.trade_cost = {
				charges: 0.0,
				consideration: 0.0,
				net_consideration: 0.0,
			};

			this.form.trade_data.forEach((equity) => {
				const { charges, consideration, net_consideration } = equity;

				this.form.trade_cost.charges += charges;
				this.form.trade_cost.consideration += consideration;
				this.form.trade_cost.net_consideration += net_consideration;
			});

			if (this.form.trade_cost.net_consideration > this.currentBalance.at_hand) {
				const topUp = parseFloat(this.form.trade_cost.net_consideration - this.currentBalance.at_hand).toFixed(2);

				trade.validation = {
					message: `There is not enough cash in this account to trade. Please top up with at least : ${this.$options.filters.toNumber(topUp)}`,
				};
			}
		},
		setTradeAction(action = 'validate') {
			this.submitTrade(action);
		},
		updateloadingTradeCounterStatus(status) {
			this.loadingTradeCounterData = status;
		},
		selectEquity(stock) {
			this.marketData.some((data) => {
				if (data.stock_code === stock && !data.selected) {
					this.showTradeCounter = true;
					this.submitted = false;
					this.loadingTradeCounterData = true;
					this.form.clear();

					axios
						.all([this.getEquityPriceBand(data.stock_code), this.getVolumeThreshold(data.price)])
						.then(
							axios.spread((equityPriceBandResponse, volumeThresholdResponse) => {
								const { equity_price_band } = equityPriceBandResponse.data.data;
								const { volume_threshold } = volumeThresholdResponse.data.data;
								const { external_portfolio_id } = this.portfolio;
								const { account_code } = this.company;

								data.selected = true;
								this.form.trade_data.push({
									external_portfolio_id,
									equity_price_band,
									volume_threshold,
									account_code,
									currency: 'GHS',
									equity: {
										stock_code: data.stock_code,
										symbol: data.symbol,
										holding: data.holding,
									},
									stock_code: data.stock_code,
									symbol: data.symbol,
									price: data.price,
									basePrice: data.price,
									type: 'BUY',
									volume: null,
									volume_status: true,
									charges: 0.0,
									consideration: 0.0,
									net_consideration: 0.0,
									order_type: 'MARKET_ORDER',
									order_source: 'PORTAL',
									market_type: 'GSE',
									auto_target: true,
								});
							})
						)
						.finally(() => (this.loadingTradeCounterData = false));
				}
			});
		},
		removeEquity({ stock, index }) {
			this.form.trade_data.splice(index, 1);
			this.showTradeCounter = !_.isEmpty(this.form.trade_data);
			this.deselectEquity(stock);
		},
		removeAll() {
			this.form.trade_data.forEach((datum) => {
				this.deselectEquity(datum.equity.stock_code);
			});

			this.form.trade_data = [];
			this.showTradeCounter = false;
		},
		deselectEquity(stock) {
			this.marketData.some((datum) => {
				if (datum.stock_code === stock) {
					datum.selected = false;
					return true;
				}
			});
		},
		getVolumeThreshold(order_price) {
			return axios.get('/api/trade/volume-threshold', {
				params: {
					order_price,
				},
			});
		},
		getEquityPriceBand(stock_code) {
			return axios.get('/api/trade/equity-price-band', {
				params: {
					stock_code,
				},
			});
		},
		getMarketData() {
			return axios.get('/api/trade/market-data');
		},
		getYearPerformances() {
			return axios.get('/api/trade/market-data/performance');
		},
		fetchData() {
			axios
				.all([this.fetchPortfolio(), this.getMarketData(), this.getYearPerformances()])
				.then(
					axios.spread((portfolioResponse, marketDataResponse, yearPerformancesResponse) => {
						const portfolio = portfolioResponse.data.data.portfolio;
						const yearPerformances = yearPerformancesResponse.data.data.performance;

						this.marketData = marketDataResponse.data.data.market_data;
						this.form.portfolio_id = portfolio.external_portfolio_id;
						this.portfolio = portfolio;

						this.marketData.forEach((data) => {
							const { year_high, year_low } = _.maxBy(yearPerformances[data.stock_code], 'value_date');

							data.holding = this.checkHolding(data.stock_code);
							data.price = this.calculatePrice(data);
							data.yearPerformance = yearPerformances[data.stock_code];
							data.year_high = year_high;
							data.year_low = year_low;
						});
					})
				)
				.finally(() => (this.loading = false));
		},
		submitTrade(type) {
			this.form.action = type;
			this.form.account_code = this.company.account_code;

			this.form
				.post('/api/trade')
				.then(() => {
					if (type === 'process') {
						this.submitted = true;
						this.isSummary = false;
						this.removeAll();
						return this.fetchData();
					}

					this.isSummary = true;
				})
				.catch(() => {
					this.error = this.form.errors.get('error');
				});
		},
	},
};
</script>

<style>
</style>